// alpine component for the history page, manages filters, sorting, and formatting
export default function historyPage(filters = {}) {
    return {
        filters,

        // stop reason multi-select state, initialized from the url filter value
        selectedStopReasons: filters.stopReason ? filters.stopReason.split(',').filter(Boolean) : [],
        stopReasonOptions: [
            {value: 'user_stop', label: 'User Stop'},
            {value: 'disconnect_timeout', label: 'Disconnect Timeout'},
            {value: 'max_time', label: 'Max Time'},
            {value: 'chrome_closed', label: 'Chrome Closed'},
        ],

        toggleStopReason(value) {
            const index = this.selectedStopReasons.indexOf(value);
            if (index === -1) {
                this.selectedStopReasons.push(value);
            } else {
                this.selectedStopReasons.splice(index, 1);
            }
        },

        isStopReasonSelected(value) {
            return this.selectedStopReasons.includes(value);
        },

        get stopReasonValue() {
            return this.selectedStopReasons.join(',');
        },

        // domain autocomplete multi-select state, initialized from the url filter value
        selectedDomains: filters.domain ? filters.domain.split(',').filter(Boolean) : [],
        domainQuery: '',
        domainSuggestions: [],
        domainDropdownOpen: false,

        // fetch domain suggestions from the server and exclude already selected ones
        async fetchDomains(query) {
            const url = '/history/domains?q=' + encodeURIComponent(query);
            // the x-alpine-target header tells alpine-ajax this is not a page navigation request
            const response = await fetch(url, {headers: {'X-Alpine-Target': 'none'}});
            if (response.ok) {
                const allDomains = await response.json();
                // filter out already selected
                this.domainSuggestions = allDomains.filter(domain => !this.selectedDomains.includes(domain));
            }
        },

        // fetch suggestions on every keystroke, show all if query is empty
        async onDomainInput() {
            if (this.domainQuery.length >= 1) {
                await this.fetchDomains(this.domainQuery);
                this.domainDropdownOpen = this.domainSuggestions.length > 0;
            } else {
                await this.fetchDomains('');
                this.domainDropdownOpen = this.domainSuggestions.length > 0;
            }
        },

        // show suggestions dropdown when the input receives focus
        async onDomainFocus() {
            await this.fetchDomains(this.domainQuery);
            this.domainDropdownOpen = this.domainSuggestions.length > 0;
        },

        // add domain to selected list and close dropdown
        addDomain(domain) {
            if (!this.selectedDomains.includes(domain)) {
                this.selectedDomains.push(domain);
            }
            this.domainQuery = '';
            this.domainDropdownOpen = false;
            this.domainSuggestions = [];
        },

        // remove domain pill from selected list
        removeDomain(domain) {
            this.selectedDomains = this.selectedDomains.filter(selectedDomain => selectedDomain !== domain);
        },

        // computed value for the hidden input, sent as comma-separated string
        get domainValue() {
            return this.selectedDomains.join(',');
        },

        // toggle filter select state, initialized from url filter values
        adblockValue: filters.adblock || '',
        captchaSolverValue: filters.captchaSolver || '',
        captchaFoundValue: filters.captchaFound || '',
        captchaSolvedValue: filters.captchaSolved || '',
        successValue: filters.success || '',
        addIpToBlockListValue: filters.addIpToBlockList || '',

        // watcher clears found/solved when captcha solver is set to off
        init() {
            this.$watch('captchaSolverValue', (value) => {
                if (value === '0') {
                    this.captchaFoundValue = '';
                    this.captchaSolvedValue = '';
                }
            });
        },

        // returns the display label for a custom select dropdown
        getSelectLabel(type, value) {
            if (type === 'onOff') {
                if (value === '1') {
                    return 'On';
                }
                if (value === '0') {
                    return 'Off';
                }
                return 'Any';
            }
            if (value === '1') {
                return 'Yes';
            }
            if (value === '0') {
                return 'No';
            }
            return 'Any';
        },

        // positions a fixed dropdown below its trigger button
        positionDropdown(triggerEl, dropdownEl) {
            const rect = triggerEl.getBoundingClientRect();
            dropdownEl.style.top = (rect.bottom + 4) + 'px';
            dropdownEl.style.left = rect.left + 'px';
            dropdownEl.style.width = rect.width + 'px';
        },

        // convert seconds to human readable duration like "1h 30m 5s"
        formatDuration(totalSeconds) {
            if (totalSeconds === null || totalSeconds === undefined) return '-';
            const hours = Math.floor(totalSeconds / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            const seconds = totalSeconds % 60;

            if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
            if (minutes > 0) return `${minutes}m ${seconds}s`;
            return `${seconds}s`;
        },

        // convert bytes to human readable size like "1.5 MB"
        formatBytes(bytes) {
            if (bytes === null || bytes === undefined || bytes === 0) return '0 B';
            const units = ['B', 'KB', 'MB', 'GB'];
            let unitIndex = 0;
            let value = bytes;

            while (value >= 1024 && unitIndex < units.length - 1) {
                value /= 1024;
                unitIndex++;
            }

            return `${value.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
        },

        // update the hidden sort inputs and submit the form
        sort(column) {
            const form = this.$el.closest('[x-data]').querySelector('form');
            const sortColumnInput = form.querySelector('[name="sortColumn"]');
            const sortDirectionInput = form.querySelector('[name="sortDirection"]');

            // toggle direction if clicking the same column, otherwise default to descending
            if (sortColumnInput.value === column) {
                sortDirectionInput.value = sortDirectionInput.value === 'ASC' ? 'DESC' : 'ASC';
            } else {
                sortColumnInput.value = column;
                sortDirectionInput.value = 'DESC';
            }

            form.requestSubmit();
        },

        // format a datetime-local value into a human-readable label like "Sat, Feb 14, 2026 3:30 PM"
        formatDateLabel(value) {
            if (!value) {
                return '';
            }
            // datetime-local sends "YYYY-MM-DDTHH:MM", parse as local time
            const date = new Date(value);
            if (isNaN(date.getTime())) {
                return '';
            }
            return date.toLocaleDateString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
            });
        },

        // disable empty inputs before submit so they don't appear in the URL
        cleanSubmit(event) {
            const form = event.target;
            for (const input of form.querySelectorAll('input')) {
                if (input.value === '' || input.value === null) {
                    input.disabled = true;
                }
            }
            // re-enable after submit so the form stays functional
            setTimeout(() => {
                for (const input of form.querySelectorAll('input')) {
                    input.disabled = false;
                }
            }, 0);
        },

        // copy a shareable url with the session id as search parameter to clipboard
        copyPermalink(sessionID) {
            const url = location.origin + '/history?search=' + encodeURIComponent(sessionID);
            if (navigator.clipboard) {
                navigator.clipboard.writeText(url);
            }
        }
    };
}
